import { GenericStatusInfoTitle } from '@caresend/types';
import { nullishFilter, objectMap, getGenericStatusInfoForDraftWaypointSupplies, getMinimalStatusInfo, getMinimalStatusInfoArray, unbindByContext } from '@caresend/utils';
import { defineComponent, computed, ref, watch, onUnmounted } from 'vue';
import __vue_component__ from './ItineraryChildStatusesCard.vue.js';
import { usePortalStore } from '../../../../../store/portal/index.js';
import { getStore } from '../../../../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ItineraryDraftWaypointSupplyStatuses',
    components: {
        ItineraryChildStatusesCard: __vue_component__,
    },
    props: {
        displayTransferLink: Boolean,
        /**
         * If true, include subtitles for draft waypoints for certain specified
         * statuses where the additional information is useful. Subtitles will
         * display horizontally - avoid if horizontal space is limited.
         */
        includeSomeSubtitles: Boolean,
        itineraryID: {
            type: String,
            default: undefined,
        },
        marginTop: Boolean,
        waypointID: {
            type: String,
            default: undefined,
        },
        requireFetch: {
            type: Boolean,
            default: true,
        },
    },
    setup(props) {
        const store = getStore();
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const waypointIDs = computed(() => {
            if (props.waypointID)
                return [props.waypointID];
            if (!props.itineraryID)
                return [];
            const waypoints = portalStore.itinerary.getters.currentWaypoints();
            return waypoints
                .filter((waypoint) => !props.waypointID || waypoint.id === props.waypointID)
                .map((waypoint) => waypoint.id);
        });
        const draftWaypointIDs = computed(() => waypointIDs.value
            .reduce((prev, waypointID) => {
            const waypoint = portalStore.waypoints.get(waypointID);
            if (!(waypoint === null || waypoint === void 0 ? void 0 : waypoint.draftWaypoints))
                return prev;
            return prev.concat(Object.keys(waypoint.draftWaypoints));
        }, [])
            .filter(nullishFilter));
        const draftWaypoints = computed(() => {
            var _a, _b;
            return Object.values(props.waypointID
                ? (_b = (_a = portalStore.waypoints.get(props.waypointID)) === null || _a === void 0 ? void 0 : _a.computed.draftWaypoints) !== null && _b !== void 0 ? _b : {}
                : portalStore.itinerary.getters.draftWaypoints(props.itineraryID));
        });
        /**
         * Draft waypoints containing supply transfers will have data stored by
         * supply transfer ID. Draft waypoints without supply transfers will have
         * data stored by draft waypoint ID.
         */
        const dataByID = computed(() => draftWaypoints.value
            .reduce((prev, draftWaypoint) => {
            const supplyTransfers = store.getters['supplies/getSupplyTransfersByDraftWaypoint'](draftWaypoint);
            const hasSupplyTransfers = Object.values(supplyTransfers).length;
            if (!hasSupplyTransfers) {
                const supplyData = { draftWaypoint };
                return {
                    ...prev,
                    [draftWaypoint.id]: supplyData,
                };
            }
            const supplyData = objectMap(supplyTransfers, (supplyTransfer) => ({
                draftWaypoint,
                supplyTransfer,
                supplyShipments: Object.values(store.getters['supplies/getSupplyShipmentsBySupplyTransfer'](supplyTransfer)),
            }));
            return {
                ...prev,
                ...supplyData,
            };
        }, {}));
        const supplyTransfersGenericStatusInfo = computed(() => Object.values(dataByID.value).reduce((prev, supplyData) => {
            const statusInfo = getGenericStatusInfoForDraftWaypointSupplies(supplyData.draftWaypoint, supplyData.supplyTransfer, supplyData.supplyShipments);
            /**
             * For minimal display, remove subtitle depending on props and status.
             * Always remove callback button.
             */
            let minimalInfo;
            if (props.includeSomeSubtitles) {
                const fullInfoStatuses = [
                    GenericStatusInfoTitle.ACTION_NEEDED,
                    GenericStatusInfoTitle.CONFIRMED,
                    GenericStatusInfoTitle.SHIPPED, // Show shipment date
                ];
                minimalInfo = statusInfo.map((infoItem) => {
                    if (fullInfoStatuses.includes(infoItem.title)) {
                        const { callBackButtonLabel, ...infoItemWithoutCallback } = infoItem;
                        return infoItemWithoutCallback;
                    }
                    return getMinimalStatusInfo(infoItem);
                });
            }
            else {
                minimalInfo = getMinimalStatusInfoArray(statusInfo);
            }
            return [...prev, ...minimalInfo];
        }, []));
        const isLoading = ref(false);
        const withoutFetchLoading = computed(() => draftWaypointIDs.value.some((id) => !portalStore.draftWaypoints.get(id)
            && store.getters['app/getIsLoading'](id))
            || draftWaypoints.value.some(({ supplyTransfers }) => Object.values(supplyTransfers !== null && supplyTransfers !== void 0 ? supplyTransfers : {}).some((st) => {
                var _a, _b;
                return (!store.state.supplies.supplyTransfers[st.id]
                    && store.getters['app/getIsLoading'](st.id))
                    || Object.keys((_b = (_a = store.state.supplies.supplyTransfers[st.id]) === null || _a === void 0 ? void 0 : _a.shipmentIDs) !== null && _b !== void 0 ? _b : {}).some((shipmentID) => !store.state.supplies.shipments[shipmentID]
                        && store.getters['app/getIsLoading'](shipmentID));
            })));
        watch(withoutFetchLoading, (curr, prev) => {
            if (curr === prev)
                return;
            isLoading.value = curr;
        }, { immediate: true });
        const to = computed(() => {
            var _a;
            const draftWaypointID = (_a = draftWaypointIDs.value[0]) !== null && _a !== void 0 ? _a : '';
            return {
                /** TODO: Implement draft waypoint modal */
                // name: routeNames.WAYPOINT_SUPPLY_LIST,
                params: {
                    draftWaypointID: draftWaypointID,
                },
            };
        });
        onUnmounted(() => {
            unbindByContext('draftWaypoints/fetchDraftWaypointDependencies');
        });
        const classNames = computed(() => {
            const names = [];
            // Only add prop-based margin if there is content to display
            if (props.marginTop
                && (isLoading.value || supplyTransfersGenericStatusInfo.value.length))
                names.push('ItineraryDraftWaypointSupplyStatuses--margin-top');
            return names;
        });
        return {
            classNames,
            isLoading,
            supplyTransfersGenericStatusInfo,
            to,
            viewAs,
        };
    },
});

export { script as default };
